"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Flame, ShieldAlert, Wrench, PhoneCall, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const services = [{
  title: "Water Heater Replacement",
  description: "Fast tank removal and same-day installation of standard gas and electric water heaters with full warranty coverage.",
  badge: "Same-Day Service",
  icon: Flame,
  link: "/water-heater/",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_replacement.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_replacement.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_replacement.png-wvc-sizes",
  features: ["Gas & electric storage tanks", "Old tank haul away included", "Code compliance check"]
}, {
  title: "Tankless Water Heater Service",
  description: "Expert installation, annual descaling, and high-efficiency repairs for all major tankless brands in Jonesboro.",
  badge: "High Efficiency",
  icon: Wrench,
  link: "/water-heater/",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/tankless_water_heater.png-wvc-sizes",
  features: ["Continuous hot water on demand", "Annual system flush & maintenance", "Space-saving wall mounts"]
}, {
  title: "Emergency Plumbing",
  description: "Immediate response for burst pipes, severe drain clogs, sewage backups, and active flooding across Clayton County.",
  badge: "24/7 Rapid Response",
  icon: ShieldAlert,
  link: "/services/",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_repair.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_repair.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_repair.png-wvc-sizes",
  features: ["Available nights and weekends", "Fast local dispatch in Jonesboro", "Fully equipped service vans"]
}];
export default function FeaturedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="featuredservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 font-semibold text-secondary-foreground">
              Jonesboro Plumbing Solutions
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Core Emergency &amp; Water Heater Services
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-sans">
              Reliable plumbing and water heating solutions for homeowners in Jonesboro, GA. We arrive fast with fully stocked service trucks.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline" className="font-medium">
              <Link to="/services/">
                View All Services
              </Link>
            </Button>
            <Button asChild variant="default" className="gap-2 bg-primary text-primary-foreground">
              <Link to="/contact/">
                <PhoneCall className="size-4" />
                Get Immediate Help
              </Link>
            </Button>
          </div>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }}>
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.title} variants={itemVariants} data-index={index}>
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                    <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="default" className="bg-primary/90 text-primary-foreground backdrop-blur-xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground font-default">
                        {service.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-muted-foreground text-sm font-sans mt-1">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-4">
                    <ul className="space-y-2 text-sm text-card-foreground">
                      {service.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-2" data-index={fIndex}>
                          <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-0 border-t border-border mt-auto pt-4 flex items-center justify-between">
                    <Button asChild variant="link" className="px-0 font-semibold text-primary group gap-1.5">
                      <Link to={service.link}>
                        Learn more
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}